import { staleTimes, useQuery } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import { LIST_PAGE_SIZE, useKeysetPager } from '../useKeysetPages.ts';
import type { OrderNarrowingQuery } from './order-filters.ts';
import type { Order, OrderStatus, OrderView } from './orders.ts';

export const ORDER_PAGE_SIZE = LIST_PAGE_SIZE;

export type OrderListScope = { readonly status: OrderStatus | null; readonly text: string };

export const WHOLE_VIEW: OrderListScope = { status: null, text: '' };

export const useOrderList = (
  api: TypedClient,
  view: OrderView,
  narrowing: OrderNarrowingQuery,
  scope: OrderListScope = WHOLE_VIEW,
) => {
  const { tenantId } = usePageContext();
  const status = scope.status === null ? {} : { status: scope.status };
  const text = scope.text.trim();
  const searched = text === '' ? {} : { text };
  return useKeysetPager<Order>({
    queryKey: queryKeys.orders(tenantId, { view, ...narrowing, ...status, ...searched }),
    resource: 'orders',
    fetchPage: (pageToken) => {
      const page = {
        page_size: LIST_PAGE_SIZE,
        view,
        ...narrowing,
        ...status,
        ...(pageToken === null ? {} : { page_token: pageToken }),
      };
      return text === ''
        ? api.get('/admin/orders', { query: page })
        : api.post('/admin/orders/search', { body: { ...page, text } });
    },
  });
};

export const useOrderViewCounts = (api: TypedClient, narrowing: OrderNarrowingQuery) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.orders(tenantId, { counts: true, ...narrowing }),
    queryFn: () => api.get('/admin/orders/views', { query: narrowing }),
    staleTime: staleTimes.orders,
    select: (data) => new Map(data.counts.map((entry) => [entry.view, entry.count])),
  });
};
